// «Rispondi a {nome}»: sotto il racconto che un parente ha lasciato con il suo link, mamma gli
// scrive due righe. Le legge lui sulla sua pagina (e per email, se l'ha chiesto). Richiesta #16,
// scritta da lei: «Come faccio a rispondere a Sabry, non vedo scritto nulla tipo "rispondi"».
//
// «Ritira» c'è finché lui non l'ha vista: dopo, sarebbe togliergli una cosa già letta.
import { api } from './api.js';
import { el, fmtDate } from './ui.js';

type Risposta = { id: number; testo: string; createdAt: string; vista: boolean; avvisato: boolean; ritirabile: boolean };
type Stato = { ospite: { nome: string } | null; max?: number; risposte: Risposta[] };

/** La carta. Torna subito un contenitore vuoto: per le storie di mamma resta vuoto. */
export function cartaRispostaOspite(memoryId: number): HTMLElement {
  const carta = el('section', { class: 'carta risposta-ospite', 'data-ruolo': 'risposta-ospite' });
  carta.hidden = true;
  const base = `/api/memories/${memoryId}/risposte-ospite`;

  const carica = async (): Promise<void> => {
    try { disegna(await api.get<Stato>(base)); } catch { /* senza la carta la storia resta com'è */ }
  };

  function disegna(s: Stato, detto?: string): void {
    if (!s.ospite) { carta.hidden = true; carta.replaceChildren(); return; }
    const nome = s.ospite.nome;
    const max = s.max ?? 2000;
    carta.hidden = false;
    carta.setAttribute('aria-label', `Rispondi a ${nome}`);

    const ta = el('textarea', {
      class: 'scrivi-testo grande', rows: '4', maxlength: String(max),
      // «Caro» o «Cara»? Non lo sappiamo: meglio un invito neutro che uno sbaglio sul suo nome.
      'aria-label': `La tua risposta a ${nome}`, placeholder: `Scrivi qui due righe per ${nome}…`,
    }) as HTMLTextAreaElement;
    const esito = el('p', { role: 'status', class: detto ? 'fatto-ok' : '' }, detto ?? '');
    const manda = el('button', { class: 'btn-verde', type: 'button' }, `Manda a ${nome} ✓`) as HTMLButtonElement;
    manda.addEventListener('click', async () => {
      const testo = ta.value.trim();
      if (!testo) { esito.className = 'avviso'; esito.textContent = `Scrivi prima qualcosa per ${nome}.`; ta.focus(); return; }
      manda.disabled = true;
      try {
        const r = await api.post<{ id: number; risposte: Risposta[] }>(base, { testo });
        disegna({ ...s, risposte: r.risposte }, `Mandata ✓ ${nome} la troverà sotto il suo racconto.`);
      } catch {
        esito.className = 'avviso';
        esito.textContent = 'Non riesco a mandarla adesso: riprova tra un momento.';
        manda.disabled = false;
      }
    });

    const precedenti = s.risposte.map((r) => {
      const stato = r.vista
        ? `${nome} l’ha letta ✓`
        : r.avvisato ? 'Mandata anche per email ✓' : `${nome} non l’ha ancora letta`;
      const ritira = r.ritirabile
        ? el('button', {
            class: 'btn-quieto', type: 'button',
            click: async () => {
              try {
                await api.post(`/api/risposte-ospite/${r.id}/ritira`);
                disegna({ ...s, risposte: s.risposte.filter((x) => x.id !== r.id) }, 'Ritirata: non la vedrà.');
              } catch {
                // Nel frattempo l'ha letta: si ridisegna com'è davvero.
                await carica();
              }
            },
          }, 'Ritira')
        : null;
      return el('li', { class: 'risposta-ospite-voce' },
        el('p', { class: 'citazione' }, `“${r.testo}”`),
        el('p', { class: 'quando' }, `${fmtDate(r.createdAt)} · ${stato}`),
        ritira,
      );
    });

    carta.replaceChildren(
      el('p', { class: 'etichetta' }, `💌 Rispondi a ${nome}`),
      precedenti.length
        ? el('div', {},
            el('p', { class: 'quando' }, `Quello che hai già scritto a ${nome}:`),
            el('ul', { class: 'risposte-ospite-lista' }, ...precedenti),
          )
        : el('p', {}, `${nome} leggerà la tua risposta sulla sua pagina, sotto il racconto che ti ha lasciato.`),
      ta,
      manda,
      esito,
    );
  }

  void carica();
  return carta;
}
